import { PaymentMapper } from '../payment/payment.mapper'
import { IOrderEntity } from '../../model/api/order/order.entity.interface';
import { IOrderSummaryEntity } from '../../model/api/order/order-summary.entity.interface';
import { ITrackOrderStatusEntity } from '../../model/api/order/track-order-status.entity.interface';
import { IOrder } from '../../model/models/order/order-interface';
import { IOrderSummary } from '../../model/models/order/order-summary-interface';
import { IValidateOrderResponse } from '../../model/models/order/validate-order-response.interface';
import { DeliveryMethodType, PayAgents } from '../../model/types/delivery-method-type';
import { PaymentMethodType } from '../../model/types/payment-method-type';
import { VopConsent } from '../../model/types/vop-type';
import { OrderStatusType } from '../../model/types/order-status-type';
import { OrderStatusEntityType } from '../../model/types/order-status-entity-type';
import { AgentMapper } from '../agent/agent.mapper';
import { IValidateOrderResponseEntity } from '../../model/api/order/validate-order-response.entity.interface';
import { ICancelOrderRequestEntity } from '../../model/api/order/cancel-order-request.interface';
import { ICancelOrderResponseEntity } from '../../model/api/order/cancel-order-response.interface';
import { ICreateOrderResponse } from '../../model/models/order/create-order-response';
import { ICreateOrderRequestEntity } from '../../model/api/order/create-order-request.entity.interface';
import { ICreateOrderResponseEntity } from '../../model/api/order/create-order-response.entity.interface';
import { ICancelOrderRequest } from '../../model/models/order/cancel-order-request.interface';
import { ICancelOrderResponse } from '../../model/models/order/cancel-order-response.interface';
import { IValidateOrderRequestEntity } from '../../model/api/order/validate-order-request.entity.interface';
import { IOrderStatusRequest } from 'model/models/order/order-status-request.interface';
import { TransactionPreview } from '../../model/myria/app/transactionPreview';
import { DELIVERY_METHOD} from '../../providers/myria/app-variables';
import { Country } from '../../model/myria/app/country';
import { UtilsProvider } from '../../providers/myria/utils-provider';
import { LoggerProvider } from '../logger/logger';
import { TranslateService } from '@ngx-translate/core';
import { VirtualPaymentType } from '../../model/types/vpa-type';
import { ResourceProvider } from '../../providers/resource/resource';

export class OrderMapper {

  public static fromOrderEntity(entity: IOrderEntity, translate: TranslateService): IOrder {
    const order = {
      confirmationExpirationTimeUtc: entity.confirmationExpirationTimeUtc,
      countryTo: entity.countryTo,
      displayCountry: entity.displayCountry,
      hasExpiryMessage: entity.hasExpiryMessage,
      shownDeliveryMethod: entity.shownDeliveryMethod,
      shownOrderStatus: entity.shownOrderStatus,
      orderStatusFromDB: entity.orderStatusFromDB,
      shownRecipientAccountNumber: entity.shownRecipientAccountNumber,
      isStaged: entity.isStaged,
      isModifiable: entity.isModifiable,
      showReceipt: entity.showReceipt,
      externalPaymentUrl: entity.externalPaymentUrl,
      additionalFields: entity.additionalFields,
      recipient: entity.recipient,
      bankAccount: entity.bankAccount ? PaymentMapper.fromBankPaymentEntity(entity.bankAccount) : null,
      cardAccount: entity.cardAccount ? PaymentMapper.fromCardPaymentEntity(entity.cardAccount) : null,
      customerTax: entity.customerTax,
      defaultIdentificationDetails: entity.defaultIdentificationDetails,
      agentTo: entity.agentTo ? AgentMapper.fromAgentEntity(entity.agentTo) : null,
      agentToLocation: entity.agentToLocation ? AgentMapper.fromAgentLocationEntity(entity.agentToLocation) : null,
      orderNumber: entity.orderNumber,
      orderId: entity.orderId,
      orderStatus: entity.orderStatus,
      queueStatus: entity.queueStatus,
      deliveryMethod: entity.deliveryMethod,
      cashPayoutType: entity.cashPayoutType,
      deliveryMethodInternal: entity.deliveryMethodInternal,
      modifyingOrderNumber: entity.modifyingOrderNumber,
      amountFrom: entity.amountFrom ? String(entity.amountFrom) : null,
      amountTo: entity.amountTo,
      customerFee: entity.customerFee,
      originalCustomerFee: entity.originalCustomerFee,
      totalAmount: entity.totalAmount ? String(entity.totalAmount) : null,
      exchangeRate: entity.exchangeRate,
      currencyFrom: entity.currencyFrom,
      currencyTo: entity.currencyTo,
      paymentMethod: entity.paymentMethod,
      transferReason: entity.transferReason,
      sourceOfFunds: entity.sourceOfFunds ? entity.sourceOfFunds : null,
      employerName: entity.employerName ? entity.employerName : null,
      businessSectorID: entity.businessSectorID ? entity.businessSectorID : null,
      incomeAmount: entity.incomeAmount ? entity.incomeAmount : null,
      incomeCurrency: entity.incomeCurrency ? entity.incomeCurrency : null,
      incomeFrequencyID: entity.incomeFrequencyID ? entity.incomeFrequencyID : null,
      incomePaymentMethodID: entity.incomePaymentMethodID ? entity.incomePaymentMethodID : null,
      assetRange: entity.assetRange ? entity.assetRange : null,
      debtRange: entity.debtRange ? entity.debtRange : null,
      incomeRange: entity.incomeRange ? entity.incomeRange : null,
      sendFrequencyPerMonth: entity.sendFrequencyPerMonth ? entity.sendFrequencyPerMonth : null,
      sendAmountPerMonth: entity.sendAmountPerMonth ? entity.sendAmountPerMonth : null,
      countryOfBirth: entity.countryOfBirth ? entity.countryOfBirth : null,
      canCancel: entity.isEligibleForCancellation,
      earlyRelease: String(entity.earlyReleased).toLowerCase() === 'true',
      number: entity.number,
      pin: entity.pin,
      status: entity.status,
      message: entity.message,
      events: entity.events,
      primaryCurrency: entity.splitCurrency1,
      secondaryCurrency: entity.splitCurrency2,
      primaryExchangeRate: entity.splitExchangeRate1,
      secondaryExchangeRate: entity.splitExchangeRate2,
      primaryReceiveAmount: entity.splitReceiveAmount1,
      secondaryReceiveAmount: entity.splitReceiveAmount2,
      isOrderModificationEligible: entity.isOrderModificationEligible,
      orderModificationDelayInSeconds: entity.orderModificationDelayInSeconds,
      isEligibleForEarlyCancellation: entity.isEligibleForEarlyCancellation === true,

      isBankDepositDeliveryMethod: entity.deliveryMethodInternal === DeliveryMethodType.BankDeposit || entity.deliveryMethodInternal === DeliveryMethodType.BankDepositNextDay,
      isOfficePickUpDeliveryMethod: entity.deliveryMethodInternal === DeliveryMethodType.OfficePickup,
      isHomeDeliveryMethod: entity.deliveryMethodInternal === DeliveryMethodType.HomeDelivery,

      isBankPaymentMethod: entity.paymentMethod === PaymentMethodType.BankAccount,
      isCardPaymentMethod: entity.paymentMethod === PaymentMethodType.CreditCard || entity.paymentMethod === PaymentMethodType.DebitCard,
      isPayNearMePaymentMethod: entity.paymentMethod === PaymentMethodType.PayNearMe,

      hasCompletedBankDepositDetails: false,

      additionalDetails: entity.additionalDetails,
      correspondentPayoutFeeAmount: entity.correspondentPayoutFeeAmount,
      correspondentPayoutFeeCurrency: entity.correspondentPayoutFeeCurrency,
      estimatedAmountToAfterCorrespondentFees: entity.estimatedAmountToAfterCorrespondentFees,
      canDownloadReceipt: (typeof(entity.canDownloadReceipt) !== 'undefined') ? entity.canDownloadReceipt : true,

      isPromoValid: entity.isPromoValid ? entity.isPromoValid  : false,
      promoAmountBonus: entity.promoAmountBonus ? entity.promoAmountBonus  : null,
      promoCode: entity.promoCode ? entity.promoCode  : null,
      promoAmountDiscount: entity.promoAmountDiscount ? entity.promoAmountDiscount  : null,
      promoInfoResponse: entity.promoInfoResponse || null,
      eta: entity.eta || null,

      uploadRequestNo: entity.uploadRequestNo ? entity.uploadRequestNo : null,
      uploadRequestStatus: entity.uploadRequestStatus ? entity.uploadRequestStatus : null,
      uploadDocumentInfo: entity.uploadDocumentInfo ? entity.uploadDocumentInfo : null,

      recipientDebitCard: entity.recipientDebitCard ? entity.recipientDebitCard : null,
      isPagoMovil: (entity.deliveryMethodDisplay === VirtualPaymentType.PagoMovilString) ? true : false,
      isSmartRouting: entity.isSmartRouting,
    }

    if (OrderMapper.isDirectDebitPayout(order)) {
      order.deliveryMethodInternal = DeliveryMethodType.DebitCard;
      order.deliveryMethod = OrderMapper.getDeliveryMethodDebitCardDeposit(translate).text;
    }

    return order;
  }

  public static fromOrderSummaryEntity(entity: IOrderSummaryEntity): IOrderSummary {
    const order: IOrderSummary = {
      pin: entity.pin?entity.pin:entity.Pin,
      orderDate: entity.orderDate?entity.orderDate:entity.Date,
      confirmationExpirationTimeUtc: entity.confirmationExpirationTimeUtc?entity.confirmationExpirationTimeUtc:entity.ExpirationDate,
      orderDateUtc: entity.orderDateUtc?entity.orderDateUtc:entity.Date,
      orderNumber: entity.orderNumber?entity.orderNumber:entity.OrderNo,

      // We do not actually have a FULL recipient here, just mapped properties from the order summary.
      // Making an incomplete object here so that we can use stuff like <rmt-recipient-image>
      //myria transactions doesn't return recipient data but customer data
      recipient: {
        id: entity.recipientId?entity.recipientId:entity.BenId,
        firstName: entity.recipientFirstName?entity.recipientFirstName:entity.BeneficiaryFirstName,
        middleName: entity.recipientMiddleName?entity.recipientMiddleName:entity.BeneficiarySecondLastName,
        lastName1: entity.recipientLastName1?entity.recipientLastName1:entity.BeneficiaryFirstLastName,
        lastName2: entity.recipientLastName2?entity.recipientLastName2:entity.BeneficiarySecondLastName,
      },
      deliveryMethod: this.mapOrderDeliveryMethod(entity) ,
      amountFrom: entity.amountFrom?entity.amountFrom:entity.AmountLocal,
      countryFrom: entity.countryFrom?entity.countryFrom:entity.CountryFrom,
      countryTo: entity.countryTo?entity.countryTo:entity.CountryTo,
      currencyTo: entity.currencyTo?entity.currencyTo:entity.Currency,
      currencyFrom: entity.currencyFrom?entity.currencyFrom:entity.PaymentCurrency,
      agentToId: entity.agentToId?entity.agentToId:entity.PayAgentId,
      agentToLocationId: entity.agentToLocationId?entity.agentToLocationId:entity.PayAgentLocId,
      estimatedDeliveryDate: entity.estimatedDeliveryDate?entity.estimatedDeliveryDate:entity.DateAvailablePayout,
      paymentMethod: entity.paymentMethod,
      queueStatus: entity.queueStatus,
      legalHold: entity.legalHold,
      ticketNumber: entity.ticketNumber,
      isModifiable: entity.isModifiable,
      isCancellable: entity.isCancellable,
      isStagedFunded: entity.isStagedFunded,
      isStaged: entity.isStaged,
      externalPaymentUrl: entity.externalPaymentUrl,
      canResend: entity.canResend,
      orderStatus: this.mapOrderStatus(entity.orderStatus?entity.orderStatus:entity.StatusId),
      orderStatusText: '',
    };

    order.orderStatusText = this.mapOrderStatusText(order.orderStatus);
    return order;
  }

  static mapOrderStatusText(status) {
    switch (status) {
      case OrderStatusType.InProcess:
      case OrderStatusType.InProgress:
      case OrderStatusType.ReadyForPickup:
      case OrderStatusType.PROCESSING_NUM_CODE:
        return 'In Progress';
      case OrderStatusType.Completed:
      case OrderStatusType.COLLECTED_BY_BENEFICIARY_NUM_CODE:
        return 'Completed';
      case OrderStatusType.Canceled:
      case OrderStatusEntityType.CANCELLED_NUM_CODE:
        return 'Canceled';
      case OrderStatusType.InReview:
      case OrderStatusType.PAYMENT_PENDING_NUM_CODE:
        return 'In Review';
      case OrderStatusType.Expired:
      case OrderStatusType.ConfirmationExpired:
        return 'Expired';
    }
    return status;
  }

  static mapOrderStatus(status: OrderStatusEntityType): OrderStatusType {
    switch (status) {
      case OrderStatusEntityType.Unfunded:
        return OrderStatusType.Unfunded;
      case OrderStatusEntityType.ReadyForPickup:
      case OrderStatusEntityType.BENEFICIARY_CAN_COLLECT_NUM_CODE:
        return OrderStatusType.ReadyForPickup;
      case OrderStatusEntityType.InProcess:
        return OrderStatusType.InProcess;
      case OrderStatusEntityType.DepositInTransit:
      case OrderStatusEntityType.DeliveryInTransit:
      case OrderStatusEntityType.Verify:
      case OrderStatusEntityType.PROCESSING_NUM_CODE:
        return OrderStatusType.InProgress;
      case OrderStatusEntityType.Deposited:
      case OrderStatusEntityType.Delivered:
      case OrderStatusEntityType.Paid:
      case OrderStatusEntityType.COLLECTED_BY_BENEFICIARY_NUM_CODE:
        return OrderStatusType.Completed;
      case OrderStatusEntityType.Canceled:
      case OrderStatusEntityType.ToCancel:
      case OrderStatusEntityType.CANCELLED_NUM_CODE:
        return OrderStatusType.Canceled;
      case OrderStatusEntityType.InReview:
      case OrderStatusEntityType.PAYMENT_PENDING_NUM_CODE:
        return OrderStatusType.InReview;
      case OrderStatusEntityType.ConfirmationExpired:
        return OrderStatusType.Expired;
    }
  }

  static mapOrderDeliveryMethod(entity: any){
    if(entity.deliveryMethod)
      return entity.deliveryMethod;
    else{
      switch (entity.DelivMethod) {
        case DeliveryMethodType.CASH_PICKUP_NUM_CODE:
          return DeliveryMethodType.OfficePickup;

        case DeliveryMethodType.BANK_DEPOSIT_NUM_CODE:
          return DeliveryMethodType.BankDeposit;

        case DeliveryMethodType.HOME_DELIVERY_NUM_CODE:
          return DeliveryMethodType.HomeDelivery;

        case DeliveryMethodType.MOBILE_WALLET_NUM_CODE:
          return DeliveryMethodType.MobilePayment;

        case DeliveryMethodType.MONEY_ORDER_NUM_CODE:
          return DeliveryMethodType.MoneyOrder;

        case DeliveryMethodType.GIFT_CARD_NUM_CODE:
          return DeliveryMethodType.GiftCard;

        case DeliveryMethodType.RURAL_PICKUP_NUM_CODE:
          return DeliveryMethodType.RuralPickUp;

        case DeliveryMethodType.BILL_PAYMENT_NUM_CODE:
          return DeliveryMethodType.BillPayment;

        case DeliveryMethodType.BILL_PAYMENT2_NUM_CODE:
          return DeliveryMethodType.BillPmt2;

        case DeliveryMethodType.BILL_PAYMENT_XPRESS_NUM_CODE:
          return DeliveryMethodType.BillPmtXPress;

        case DeliveryMethodType.PHONE_CARD_NUM_CODE:
          return DeliveryMethodType.PhoneCard;

        case DeliveryMethodType.DEBIT_CARD_NUM_CODE:
          return DeliveryMethodType.DebitCard;

        case DeliveryMethodType.BANK_DEPOSIT_NEXT_DAY_NUM_CODE:
          return DeliveryMethodType.BankDeposit;

        case DeliveryMethodType.ATM_PICKUP_NUM_CODE:
          return DeliveryMethodType.AtmPickup;

        case DeliveryMethodType.GIFT_VOUCHER_NUM_CODE:
          return DeliveryMethodType.GiftVoucher;

        case DeliveryMethodType.UPI_NUM_CODE:
          return DeliveryMethodType.UPI;

        case DeliveryMethodType.Alipay_NUM_CODE:
          return DeliveryMethodType.Alipay;

        case DeliveryMethodType.PIX_NUM_CODE:
          return DeliveryMethodType.PIX;

        case DeliveryMethodType.BreB_NUM_CODE:
          return DeliveryMethodType.BreB;

      }
    }
  }

  public static fromOrderSummariesEntity(entities: IOrderSummaryEntity[]): IOrderSummary[] {
    const orders: IOrderSummary[] = [];
    if (!entities) return [];
    entities.forEach(order => orders.push(this.fromOrderSummaryEntity(order)));
    return orders;
  }

  public static toApiOrderRequestEntity(model: IOrder, selfServicePspPaymentMethod = null): IValidateOrderRequestEntity {
    let paymentMethodId;
    // VoP consent applies only to SEPA bank deposit never carry it into other delivery methods
    const isBankDepositDelivery = model.deliveryMethodInternal === DeliveryMethodType.BankDeposit
      || model.deliveryMethodInternal === DeliveryMethodType.BankDepositNextDay;
    switch (model.paymentMethod) {
      case PaymentMethodType.BankAccount:
        if (model.bankAccount)
          paymentMethodId = model.bankAccount.accountId;
        break;
      case PaymentMethodType.CreditCard:
      case PaymentMethodType.DebitCard:
        if (model.cardAccount)
          paymentMethodId = model.cardAccount.accountId;
        break;
    }

    var params = {
      countryTo: model.countryTo,
      currencyTo: model.currencyTo,
      state: model.recipient.state,
      city: model.recipient.city,
      currencyFrom: model.currencyFrom,
      amountFrom: model.amountFrom ? String(model.amountFrom) : null,
      amountTo:  model.amountTo ? String(model.amountTo) : null,
      customerFee: model.customerFee,
      totalAmount: model.totalAmount ? String(model.totalAmount) : null,
      shouldCalcAmountFrom: model.shouldCalcAmountFrom,
      shouldCalcVariableRates: model.shouldCalcVariableRates,
      deliveryMethod: this.mapApiDeliveryMethod(<DeliveryMethodType>model.deliveryMethodInternal),
      paymentMethod: model.paymentMethod,
      recipientId: model.recipient ? model.recipient.id : null,
      paymentAccountId: paymentMethodId,
      agentToId: model.agentTo ? model.agentTo.id : null,
      agentToLocationId: model.agentToLocation ? model.agentToLocation.locationId : null,
      agentToLocationCityName: model.agentToLocation ? model.agentToLocation.locationCity : null,
      // A smart-routed bank deposit sends the payout bank, ahead of the sender's payment account.
      bankId: model.bankDepositProvider ? model.bankDepositProvider.bankId
        : model.bankAccount ? model.bankAccount.accountId
        : (model.wallet && model.wallet.EntityID) ? model.wallet.EntityID : null,
      // A mobile payment sends the wallet's format, never the recipient's saved bank format.
      bankAccountFormatId: model.deliveryMethodInternal === DeliveryMethodType.MobilePayment
        ? ((model.wallet && model.wallet.AccountFormatId) ? model.wallet.AccountFormatId : null)
        : model.recipient ? model.recipient.bankAccountFormatId : null,
      transferReason: model.transferReason,
      sourceOfFunds: model.sourceOfFunds ? model.sourceOfFunds : null,
      employerName: model.employerName ? model.employerName : null,
      businessSectorID: model.businessSectorID ? model.businessSectorID : null,
      incomeAmount: model.incomeAmount ? model.incomeAmount : null,
      incomeCurrency: model.incomeCurrency ? model.incomeCurrency : null,
      incomeFrequencyID: model.incomeFrequencyID ? model.incomeFrequencyID : null,
      incomePaymentMethodID: model.incomePaymentMethodID ? model.incomePaymentMethodID : null,
      assetRange: model.assetRange ? model.assetRange : null,
      debtRange: model.debtRange ? model.debtRange : null,
      incomeRange: model.incomeRange ? model.incomeRange : null,
      sendFrequencyPerMonth: model.sendFrequencyPerMonth ? model.sendFrequencyPerMonth : null,
      sendAmountPerMonth: model.sendAmountPerMonth ? model.sendAmountPerMonth : null,
      countryOfBirth: model.countryOfBirth ? model.countryOfBirth : null,
      deviceFingerprintSessionId: model.deviceFingerprintSessionId,
      cardSecurityCode: model.cardAccount ? model.cardAccount.cvn ? model.cardAccount.cvn : "" : "",
      promoCode: (model.promoCode) ? model.promoCode : model.promoEngineCode,
      modifyingOrderNumber: model.modifyingOrderNumber,
      promoId: model.promoId,
      isReferralPromo: model.IsReferralPromo,
      promoTransIdLocked: model.PromoTransIdLocked,
      nonThirdPartyDisclaimer: model.nonThirdPartyDisclaimer ? model.nonThirdPartyDisclaimer : null,
      isPadAgreementAccepted: model.isPadAgreementAccepted ? model.isPadAgreementAccepted : null,
      payerAuthSetupReferenceId: model.payerAuthSetupReferenceId,
      pspPaymentMethod: selfServicePspPaymentMethod ? selfServicePspPaymentMethod : model.pspPaymentMethod,
      pspPaymentSessionId: model.pspPaymentSessionId,
      pspPaymentSessionResult: model.pspPaymentSessionResult,
      pspPaymentSessionData: model.pspPaymentSessionData,
      accertifyDeviceId: model.accertifyDeviceId,
      sessionCartToken: LoggerProvider.globalSessionId,

      customerAddress: model.customerAddress,
      customerPostalCode: model.customerPostalCode,
      customerCity: model.customerCity,
      customerUnitNumber: model.customerUnitNumber,
      customerProvince: model.customerProvince,
      customerOccupationId: model.customerOccupationId,
      shouldCalcFeesForPaymentMethods: model.shouldCalcFeesForPaymentMethods,

      recipientDebitCard: model.recipientDebitCard,
      // bankDepositProvider is only set once every gate passed, so the flag and id cannot disagree.
      IsSmartRouting: !!(model.bankDepositProvider || (model.wallet && model.wallet.EntityID)),
      providerId: model.bankDepositProvider ? model.bankDepositProvider.providerId
        : (model.wallet && model.wallet.ProviderId) ? model.wallet.ProviderId : null,
      exchangeRate: model.exchangeRate ? String(model.exchangeRate) : null,
      exchangeRatePromo: model.exchangeRatePromo ? String(model.exchangeRatePromo) : null,
      vopConsent: (isBankDepositDelivery && model.vopConsent) ? model.vopConsent : VopConsent.None,
      vopFingerprint: (isBankDepositDelivery && model.vopFingerprint) ? model.vopFingerprint : null,
    };

    if (!params.agentToId && model.agentToLocation && model.agentToLocation.agentId && model.deliveryMethodInternal === DeliveryMethodType.DebitCard) {
      params.agentToId = model.agentToLocation.agentId;
    }

    if (model.paymentMethod === PaymentMethodType.StagedRia) {
      return params;
    } else {
      return { ...params, reSendOrderId: model.orderId };
    }
  }

  public static fromOrderValidationResponseEntity(entity: IValidateOrderResponseEntity): IValidateOrderResponse {
    return entity;
  }

  public static toTrackOrderStatusEntity(model: IOrderStatusRequest): ITrackOrderStatusEntity {
    return {
      orderNumber: model.orderNumber,
      getOriginalPayAgent: model.getOriginalPayAgent,
      getEta: model.getEta,
      getEventDetails: model.getEventDetails,
    };
  }

  public static toCreateOrderRequestEntity(model: IOrder): ICreateOrderRequestEntity {    
    return {
      transactionId: model.transactionId,
      deviceVerificationId: model.deviceVerificationId,
      deviceFingerprintSessionId: model.deviceFingerprintSessionId,
      cardSecurityCode: model.cardAccount ? model.cardAccount.cvn ? model.cardAccount.cvn : "" : "",
      payerAuthenticationResponse: model.payerAuthenticationResponse,
      referralCode: model.IsReferralPromo ? model.promoCode : null,
      modifyingOrderNumber: model.modifyingOrderNumber,
      nonThirdPartyDisclaimer: model.nonThirdPartyDisclaimer ? model.nonThirdPartyDisclaimer : null,
      isPadAgreementAccepted: model.isPadAgreementAccepted ? model.isPadAgreementAccepted : null,
      paTransactionId: model.paTransactionId,
      pspPaymentMethod: model.pspPaymentMethod,
      pspPaymentSessionId: model.pspPaymentSessionId,
      pspPaymentSessionResult: model.pspPaymentSessionResult,
      pspPaymentSessionData: model.pspPaymentSessionData,
      paymentAccountId: model.paymentAccountId,
      pspActionType: model.pspActionType,
      pspDeviceCollectionReferenceId: model.pspDeviceCollectionReferenceId,
      accertifyDeviceId: model.accertifyDeviceId,
      promoType: model.promoType,
      promoSourcePlatform: model.promoSourcePlatform,
      promoCode: model.promoCode ? model.promoCode : (model.promoEngineCode ? model.promoEngineCode : model.promoName),
      sessionCartToken: LoggerProvider.globalSessionId,
      OldAccountId: model.oldAccountId,
      doNotReusePaymentAccount: model.doNotReusePaymentAccount ? model.doNotReusePaymentAccount : false,
      recipientDebitCard: model.recipientDebitCard,
      isSmartRouting: !!(model.bankDepositProvider || (model.wallet && model.wallet.EntityID)),
      exchangeRate: model.exchangeRate ? String(model.exchangeRate) : null,
      exchangeRatePromo: model.exchangeRatePromo ? String(model.exchangeRatePromo) : null,
    }
  }

  public static fromCreateOrderResponseEntity(entity: ICreateOrderResponseEntity): ICreateOrderResponse {
    return {
      orderNumber: entity.orderNumber,
      status: entity.status,
      isDecisioningEnabled: entity.isDecisioningEnabled,
      pastOrderCount: entity.pastOrderCount,
      isFraudReview: entity.isFraudReview,
      isComplianceLegalHold: entity.isComplianceLegalHold,
      isComplianceReview: entity.isComplianceReview,
      isAchVerified: entity.isAchVerified,
      isAchEarlyRelease: entity.isAchEarlyRelease,
      deliveryMethod: entity.deliveryMethod,
      enablePspSDKFlow: entity.enablePspSDKFlow,
      isCreditOrDebitPaymentMethod: entity.isCreditOrDebitPaymentMethod,
      isConfirmed: entity.isConfirmed,
      pin: entity.pin,
      uploadRequestNo: entity.uploadRequestNo,
      uploadDocumentInfo: entity.uploadDocumentInfo,
      uploadReason: entity.uploadReason,
      uploadDocTypes: entity.uploadDocTypes,
      isEarlyReleaseIntervention: entity.isEarlyReleaseIntervention,
      feeDifferenceACHvsDebit: entity.feeDifferenceACHvsDebit,
      earlyReleaseIntervenedPreOrderId: entity.earlyReleaseIntervenedPreOrderId,
      pspAction: entity.pspAction,
      pspActionStage: entity.pspActionStage,
      pspAddedAccountId: entity.pspAddedAccountId,
      orderPending: entity.orderPending,
      fraudVerificationReviewType: entity.fraudVerificationReviewType,
      updateAddress: entity.updateAddress ? entity.updateAddress : false,
      requiresOtpVerification: entity.requiresOtpVerification ? entity.requiresOtpVerification : false,
      maskedPhoneNumber: entity.maskedPhoneNumber ? entity.maskedPhoneNumber : null,
      fraudOtpPreOrderId: entity.fraudOtpPreOrderId ? entity.fraudOtpPreOrderId : null,
      otpRemainingAttempts: entity.otpRemainingAttempts ? entity.otpRemainingAttempts : null,
      exceededInvalidOtpAttempts: entity.exceededInvalidOtpAttempts ? entity.exceededInvalidOtpAttempts : null,
      waitingOtpTime: entity.waitingOtpTime ? entity.waitingOtpTime : null,
      code: entity.code ? entity.code : null,
      additionalDetails: entity.additionalDetails ? entity.additionalDetails : null,
      scamAlertResponse: entity.scamAlertResponse ? {
        requiresScamRiskConfirmation: !!entity.scamAlertResponse.requiresScamRiskConfirmation,
        scamAlertPreOrderId: entity.scamAlertResponse.scamAlertPreOrderId || null,
        scamRiskTitle: entity.scamAlertResponse.scamRiskTitle || null,
        scamRiskDescription: entity.scamAlertResponse.scamRiskDescription || null,
        scamRiskLabel: entity.scamAlertResponse.scamRiskLabel || null,
        genericScamAlertResponse: entity.scamAlertResponse.genericScamAlertResponse ? {
          scamAlertQuestion: entity.scamAlertResponse.genericScamAlertResponse.scamAlertQuestion || null,
          genericScamAlertData: (entity.scamAlertResponse.genericScamAlertResponse.genericScamAlertData || []).map((item) => ({
            scamRiskLabel: item.scamRiskLabel || null,
            scamRiskTextForGenericSelections: item.scamRiskTextForGenericSelections || null,
            scamRiskTitle: item.scamRiskTitle || null,
            scamRiskDescription: item.scamRiskDescription || null
          }))
        } : null
      } : null
    };
  }

  public static toCancelOrderRequestEntity(model: ICancelOrderRequest): ICancelOrderRequestEntity {
    return {
      orderNumber: model.orderNumber,
      cancelReason: model.cancelReason
    };
  }

  public static fromCancelOrderResponseEntity(entity: ICancelOrderResponseEntity): ICancelOrderResponse {
    return <ICancelOrderResponse>entity;
  }

  public static mapOrder(order: IOrder, transaction: TransactionPreview, destinationCountry: Country, utilsProvider: UtilsProvider): any{
    order.amountFrom = utilsProvider.replaceAmountAccordingLanguageNewOrder(transaction.SendAmount, transaction.DecimalsMonetaryFrom);
    order.amountTo = utilsProvider.replaceAmountAccordingLanguageNewOrder(transaction.PayoutAmount, transaction.DecimalsMonetaryTo);
    order.customerFee = transaction.CustomerFee;
    order.totalAmount = utilsProvider.replaceAmountAccordingLanguageNewOrder(transaction.TotalAmount, transaction.DecimalsMonetaryFrom);
    order.exchangeRate = transaction.ExchangeRate;
    order.isPromoValid = transaction.IsPromoValid;
    order.promoAmount = transaction.PromocodeFee;
    order.promoCode = transaction.PromoCode;
    order.currencyFrom = transaction.SendCurrency;
    order.currencyTo = transaction.PayoutCurrency;
    // //order.deliveryMethod = (transaction.PayoutMethod == DELIVERY_METHOD.BANK_DEPOSIT.Key) ? DeliveryMethodType.BankDeposit : (transaction.PayoutMethod == DELIVERY_METHOD.CASH_PICKUP.Key) ? DeliveryMethodType.OfficePickup : DeliveryMethodType.HomeDelivery;//transaction.PayoutMethod;
    order.countryFrom = transaction.PayoutCountry;
    order.bankAccount = null;//this.transaction.BankAccountNo;
    order.cardAccount = null;
    order.isBankDepositDeliveryMethod = transaction.PayoutMethod == DELIVERY_METHOD.BANK_DEPOSIT.Key;
    order.isOfficePickUpDeliveryMethod = transaction.PayoutMethod == DELIVERY_METHOD.CASH_PICKUP.Key;
    order.isHomeDeliveryMethod = transaction.PayoutMethod == DELIVERY_METHOD.HOME_DELIVERY.Key;
    order.recipient = null;
    order.transactionId = null;
    order.deviceVerificationId = null;
    order.deviceFingerprintSessionId = null;
    order.customerTax = transaction.Tax;
    order.agentToLocation = {
      agentId: (destinationCountry) ? destinationCountry.DefaultOpenPaymentPayAgentId : null,
      agentName: transaction.PayoutCorrespName,
      locationId: (destinationCountry) ? destinationCountry.DefaultOpenPaymentPayAgentLocId : null,
      locationName: transaction.PayoutLocName,
      locationAddress: transaction.PayoutLocAddress1,
      currency: transaction.PayoutCurrency
    };
    // order.agentTo = null;
    //order.paymentMethod = PaymentMethodType.PayNearMe;

    order.primaryCurrency = transaction.SplitCurrency1;
    order.secondaryCurrency = transaction.SplitCurrency2;
    order.primaryExchangeRate = transaction.SplitExchangeRate1;
    order.secondaryExchangeRate = transaction.SplitExchangeRate2;
    order.primaryReceiveAmount = transaction.SplitReceiveAmount1;
    order.secondaryReceiveAmount = transaction.SplitReceiveAmount2;

    return order;
  }

  public static fromValidateOrderEntity(entity: IOrder): IOrder {
    const order = {
      amountTo: entity.amountTo,
      beneficiaryFirstName: entity.beneficiaryFirstName,
      beneficiaryLastName: entity.beneficiaryLastName,
      beneficiaryMiddleName: entity.beneficiaryMiddleName,
      currencyTo: entity.currencyTo,
      events: entity.events,
      orderNumber: entity.orderNumber,
      orderStatus: entity.orderStatus,
      pin: entity.pin,
    }

    return order;
  }

  public static mapApiDeliveryMethod(deliveryMethod: DeliveryMethodType) : DeliveryMethodType {
    return (deliveryMethod === DeliveryMethodType.DebitCard) ? DeliveryMethodType.BankDeposit : deliveryMethod;
  }

  public static effectiveDeliveryMethod(model: IOrder) : DeliveryMethodType {
    return (model.deliveryMethodAlias === DeliveryMethodType.RiaWallet
        && model.deliveryMethodInternal === DeliveryMethodType.MobilePayment)
      ? DeliveryMethodType.RiaWallet
      : <DeliveryMethodType>model.deliveryMethodInternal;
  }

  public static isDirectDebitPayoutAgent(agentId) {
    return (agentId === PayAgents.VisaDebitCard);
  }

  public static isDirectDebitPayout(order) {
    return (
      order.deliveryMethodInternal === DeliveryMethodType.BankDeposit &&
      order.agentTo &&
      OrderMapper.isDirectDebitPayoutAgent(order.agentTo.id)
    );
  }

  public static getDeliveryMethodDebitCardDeposit(translate) {
    return {
      text: translate.instant('form.visaDirect.deliveryMethodName'),
      value: DeliveryMethodType.DebitCard.toString()
    };
  }
}


// WEBPACK FOOTER //
// ./src/providers/order/order.mapper.ts